import { useFormContext } from 'react-hook-form'
import { IntegrationServiceId } from '../../../../../../../common/integrations'
import { IntegrationSettings } from '../../../schema'
import { useMemo, useState } from 'react'
import { SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { CustomObjectMapping } from '../../../../../../../models/Account'
import { CustomObjectBody } from './CustomObjectBody'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../../store/hooks'
import { useGetSupportedObjectsQuery } from '../../../../../../../store/integrations/integrations'
import { useGetIntegrationAdminSettings } from '../../../hooks'
import { ObjectRowShell } from '../shared/ObjectRowShell'

interface CustomObjectRowProps {
  serviceId: IntegrationServiceId
  index: number
  defaultExpanded?: boolean
}

export const CustomObjectRow: React.FC<CustomObjectRowProps> = ({ serviceId, index, defaultExpanded = false }) => {
  const { setValue, watch } = useFormContext<IntegrationSettings>()

  const [activeKey, setActiveKey] = useState<string | null>(() => (defaultExpanded ? String(index) : null))

  const accountId = useAppSelector(selectAccountId)
  const { hasIntegration } = useGetIntegrationAdminSettings(serviceId)

  const { data: supportedObjects, isLoading: isLoadingSupportedObjects } = useGetSupportedObjectsQuery(
    { accountId, connector: serviceId },
    { skip: !accountId || !serviceId || !hasIntegration },
  )
  const customObjects = watch('mappings.customObjects') as CustomObjectMapping[]

  const currentObject = customObjects[index]
  const objectName = useMemo(() => currentObject?.objectName, [currentObject])

  const usedObjectNames = useMemo(() => new Set(customObjects.filter((_, i) => i !== index).map((obj) => obj.objectName)), [customObjects, index])
  const options = useMemo(() => supportedObjects?.map((obj) => ({ id: obj.name, label: `${obj.label} (${obj.name})` })) || [], [supportedObjects])

  const handleObjectSelect = (option: SelectOption) => {
    setValue(
      `mappings.customObjects.${index}`,
      {
        ...customObjects[index],
        objectName: option.id,
        displayName: supportedObjects?.find((obj) => obj.name === option.id)?.label ?? option.id,
        fields: [],
        referenceFields: [],
        contactLinks: [],
        filterGroup: null,
      },
      { shouldDirty: true, shouldValidate: true },
    )
  }

  const handleDelete = () => {
    setActiveKey(null)

    const currentList = customObjects || []
    const updatedList = currentList.filter((_, i) => i !== index)
    setValue('mappings.customObjects', updatedList, { shouldDirty: true, shouldValidate: true })
  }

  const handleAccordionSelect = (eventKey: string | null | undefined) => {
    if (!currentObject?.objectName) {
      return
    }
    if (eventKey === String(index)) {
      setActiveKey(String(index))
    } else {
      setActiveKey(null)
    }
  }

  return (
    <ObjectRowShell
      index={index}
      activeKey={activeKey}
      onAccordionSelect={handleAccordionSelect}
      options={options}
      excludeObjectNames={usedObjectNames}
      selectedObjectName={objectName}
      onOptionSelected={handleObjectSelect}
      loading={isLoadingSupportedObjects}
      menuItems={[{ label: 'Delete', icon: 'bi-trash', onClick: handleDelete, danger: true }]}
      body={currentObject?.objectName ? <CustomObjectBody serviceId={serviceId} objectIndex={index} /> : null}
    />
  )
}
